import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabase.js';

interface Template {
  cafeName: string;
  header: string;
  footer: string;
  /** Character columns on the paper roll (32 = 58 mm, 42 = 80 mm). */
  paperCols: number;
  printFontSize: 'normal' | 'large' | 'xlarge';
  printBold: boolean;
}

interface TemplateRow {
  cafe_name: string;
  header: string;
  footer: string;
  paper_cols: number;
  print_font_size: string;
  print_bold: boolean;
}

const DEFAULT: Template = {
  cafeName: 'ORIO CAFE',
  header: '',
  footer: 'Thank you! Շնորհակալ!',
  paperCols: 32,
  printFontSize: 'large',
  printBold: true,
};

function center(s: string, width: number): string {
  const pad = Math.max(0, width - s.length);
  return ' '.repeat(Math.floor(pad / 2)) + s;
}

function previewFontStyle(tpl: Template): CSSProperties {
  const scale =
    tpl.printFontSize === 'xlarge' ? 1.35 : tpl.printFontSize === 'large' ? 1.15 : 1;
  return {
    fontSize: `${12 * scale}px`,
    fontWeight: tpl.printBold ? 600 : 400,
  };
}

function buildPreview(tpl: Template, width: number): string {
  const effWidth = tpl.printFontSize === 'xlarge' ? Math.floor(width / 2) : width;
  const sep = '='.repeat(effWidth);
  const thin = '-'.repeat(effWidth);
  const lines: string[] = [sep, center(tpl.cafeName, effWidth)];
  if (tpl.header.trim()) {
    for (const l of tpl.header.split('\n')) lines.push(l.trim());
  }
  lines.push(sep);
  lines.push(`Order #42   06/06/2026 14:30:00`);
  lines.push(thin);
  const item = '2x Cappuccino';
  const price = '2,400 ֏';
  const pad = Math.max(1, effWidth - item.length - price.length);
  lines.push(`${item}${' '.repeat(pad)}${price}`);
  lines.push(`  [+ Oat milk]`);
  lines.push(thin);
  const totalLabel = 'TOTAL:';
  const totalVal = '2,400 ֏';
  const tpad = Math.max(1, effWidth - totalLabel.length - totalVal.length);
  lines.push(`${totalLabel}${' '.repeat(tpad)}${totalVal}`);
  lines.push(sep);
  if (tpl.footer.trim()) {
    for (const l of tpl.footer.split('\n')) lines.push(l.trim());
  }
  lines.push(sep);
  return lines.join('\n');
}

export function ReceiptTemplateManager({ clientId }: { clientId: string | null }) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Template>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!supabase || !clientId) {
      setLoading(false);
      return;
    }
    void (async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error: err } = (await (supabase as any)
        .from('receipt_templates')
        .select('cafe_name, header, footer, paper_cols, print_font_size, print_bold')
        .eq('client_id', clientId)
        .maybeSingle()) as { data: TemplateRow | null; error: { message: string } | null };
      if (err) setError(err.message);
      else if (data)
        setForm({
          cafeName: data.cafe_name,
          header: data.header,
          footer: data.footer,
          paperCols: data.paper_cols ?? DEFAULT.paperCols,
          printFontSize:
            data.print_font_size === 'normal' ||
            data.print_font_size === 'large' ||
            data.print_font_size === 'xlarge'
              ? data.print_font_size
              : DEFAULT.printFontSize,
          printBold: data.print_bold ?? DEFAULT.printBold,
        });
      setLoading(false);
    })();
  }, [clientId]);

  async function save() {
    if (!supabase || !clientId || saving) return;
    setSaving(true);
    setError(null);
    const row = {
      client_id: clientId,
      cafe_name: form.cafeName,
      header: form.header,
      footer: form.footer,
      paper_cols: form.paperCols,
      print_font_size: form.printFontSize,
      print_bold: form.printBold,
      updated_at: new Date().toISOString(),
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: err } = (await (supabase as any)
      .from('receipt_templates')
      .upsert(row, { onConflict: 'client_id' })) as { error: { message: string } | null };
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSaved(true);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setSaved(false), 3000);
  }

  if (loading) return <p className="muted">{t('common.loading')}</p>;

  return (
    <div style={{ maxWidth: 960, padding: '2rem' }}>
      {/* Page header */}
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{t('receiptTemplate.title')}</h2>
        <p className="muted" style={{ margin: '4px 0 0' }}>
          {t('receiptTemplate.subtitle')}
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '2rem',
          alignItems: 'start',
        }}
      >
        {/* ── Editor card ── */}
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.25rem',
          }}
        >
          <div>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text-subtle)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 6,
              }}
            >
              {t('receiptTemplate.cafeName')}
            </label>
            <input
              style={{ width: '100%', boxSizing: 'border-box' }}
              value={form.cafeName}
              maxLength={32}
              onChange={(e) => setForm((f) => ({ ...f, cafeName: e.target.value }))}
            />
            <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-subtle)' }}>
              {form.cafeName.length}/{form.paperCols} — centred on the receipt
            </p>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text-subtle)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 6,
              }}
            >
              {t('receiptTemplate.paperWidth')}
            </label>
            <select
              style={{ width: '100%', boxSizing: 'border-box' }}
              value={form.paperCols}
              onChange={(e) => setForm((f) => ({ ...f, paperCols: Number(e.target.value) }))}
            >
              <option value={32}>58 mm — 32 columns</option>
              <option value={42}>80 mm — 42 columns</option>
            </select>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text-subtle)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 6,
              }}
            >
              {t('receiptTemplate.printFontSize')}
            </label>
            <select
              style={{ width: '100%', boxSizing: 'border-box' }}
              value={form.printFontSize}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  printFontSize: e.target.value as Template['printFontSize'],
                }))
              }
            >
              <option value="normal">{t('receiptTemplate.fontNormal')}</option>
              <option value="large">{t('receiptTemplate.fontLarge')}</option>
              <option value="xlarge">{t('receiptTemplate.fontXlarge')}</option>
            </select>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-subtle)' }}>
              {t('receiptTemplate.printFontSizeHint')}
            </p>
          </div>

          <div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={form.printBold}
                onChange={(e) => setForm((f) => ({ ...f, printBold: e.target.checked }))}
              />
              {t('receiptTemplate.printBold')}
            </label>
            <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-subtle)' }}>
              {t('receiptTemplate.printBoldHint')}
            </p>
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text-subtle)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 6,
              }}
            >
              {t('receiptTemplate.header')}
              <span style={{ fontWeight: 400, marginLeft: 6, textTransform: 'none', fontSize: 11 }}>
                ({t('common.optional')})
              </span>
            </label>
            <textarea
              style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
              rows={3}
              placeholder={t('receiptTemplate.headerPlaceholder')}
              value={form.header}
              onChange={(e) => setForm((f) => ({ ...f, header: e.target.value }))}
            />
          </div>

          <div>
            <label
              style={{
                display: 'block',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text-subtle)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 6,
              }}
            >
              {t('receiptTemplate.footer')}
              <span style={{ fontWeight: 400, marginLeft: 6, textTransform: 'none', fontSize: 11 }}>
                ({t('common.optional')})
              </span>
            </label>
            <textarea
              style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
              rows={3}
              placeholder={t('receiptTemplate.footerPlaceholder')}
              value={form.footer}
              onChange={(e) => setForm((f) => ({ ...f, footer: e.target.value }))}
            />
          </div>

          {error && (
            <p
              style={{
                margin: 0,
                fontSize: 12,
                color: 'var(--danger-hover)',
                background: 'var(--danger-dim)',
                border: '1px solid var(--danger-border)',
                borderRadius: 6,
                padding: '8px 12px',
              }}
            >
              {error}
            </p>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button className="btn" onClick={() => void save()} disabled={saving}>
              {saving ? '…' : t('receiptTemplate.save')}
            </button>
            {saved && (
              <span
                style={{
                  fontSize: 13,
                  color: 'var(--success)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                ✓ {t('receiptTemplate.saved')}
              </span>
            )}
          </div>
        </div>

        {/* ── Preview card ── */}
        <div>
          <p
            style={{
              margin: '0 0 10px',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--text-subtle)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {t('receiptTemplate.preview')}
          </p>
          {/* Thermal paper card */}
          <div
            style={{
              background: '#faf8f0',
              color: '#111',
              borderRadius: 4,
              padding: '20px 24px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.1)',
              position: 'relative',
            }}
          >
            {/* torn-edge notches */}
            <div
              style={{
                position: 'absolute',
                top: -1,
                left: 0,
                right: 0,
                height: 6,
                backgroundImage:
                  'repeating-linear-gradient(90deg,transparent,transparent 7px,var(--bg) 7px,var(--bg) 8px)',
              }}
            />
            <div
              style={{
                position: 'absolute',
                bottom: -1,
                left: 0,
                right: 0,
                height: 6,
                backgroundImage:
                  'repeating-linear-gradient(90deg,transparent,transparent 7px,var(--bg) 7px,var(--bg) 8px)',
              }}
            />
            <pre
              style={{
                margin: 0,
                fontFamily: "'Courier New', Courier, monospace",
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                ...previewFontStyle(form),
              }}
            >
              {buildPreview(form, form.paperCols)}
            </pre>
          </div>
          <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--text-subtle)' }}>
            Live preview — updates as you type
          </p>
        </div>
      </div>
    </div>
  );
}
